import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import { StatusBadge } from "components/ui/StatusBadge";
import { Table } from "components/ui/Table/Table";
import type {
  AdminMatchingFeedbackItem,
  UseAdminMatchingFeedbacksApiOptions,
} from "hooks/useAdminApi";
import type { CSSProperties, FC } from "react";
import { Link } from "react-router-dom";
// 評価日時は他画面と同じくハブ (utils/relative-time) 経由で JST 固定表示する。
import { formatDateTime } from "utils/relative-time";

type SortKey = NonNullable<UseAdminMatchingFeedbacksApiOptions["sort"]>;
type SortOrder = NonNullable<UseAdminMatchingFeedbacksApiOptions["order"]>;

type FeedbackTableProps = {
  items: AdminMatchingFeedbackItem[];
  loading: boolean;
  sort: SortKey;
  order: SortOrder;
  onSortChange: (sort: SortKey, order: SortOrder) => void;
};

const COL_COUNT = 11;
const COMMENT_PREVIEW_LEN = 60;

/**
 * コメント文字列を抜粋表示用に整形する (コードポイント単位で安全に切る)
 * @param {string | null} comment コメント原文
 * @return {string} 60 文字 + ellipsis or "—"
 */
function previewComment(comment: string | null): string {
  if (!comment) return "—";
  // Array.from は UTF-16 サロゲートペアを正しく 1 つのコードポイントに扱う。
  // 絵文字や一部漢字を境界で分断して � になるのを防ぐ。
  const codepoints = Array.from(comment);
  if (codepoints.length <= COMMENT_PREVIEW_LEN) return comment;
  return `${codepoints.slice(0, COMMENT_PREVIEW_LEN).join("")}…`;
}

// 結果ステータスの日本語ラベルマップ
const STATUS_LABELS: Record<string, string> = {
  new: "未読",
  viewed: "閲覧済",
  proposed: "提案済",
  dismissed: "却下",
};

/**
 * 結果ステータスを soft tone のバッジに割り当てる
 * @param {string} status ステータス文字列
 * @return {"soft-info" | "soft-neutral" | "soft-success" | "soft-warning"} バッジトーン
 */
function statusTone(
  status: string,
): "soft-info" | "soft-neutral" | "soft-success" | "soft-warning" {
  switch (status) {
    case "proposed":
      return "soft-success";
    case "dismissed":
      return "soft-warning";
    case "viewed":
      return "soft-info";
    default:
      return "soft-neutral";
  }
}

/**
 * source_type を表示用ラベル + tone に変換する
 * @param {string} sourceType "hr" | "matter"
 * @return {object} label / tone を持つ表示情報
 */
function sourceTypeBadge(sourceType: string): {
  label: string;
  tone: "soft-info" | "soft-success" | "soft-neutral";
} {
  if (sourceType === "matter") {
    return { label: "案件", tone: "soft-info" };
  }
  if (sourceType === "hr") {
    return { label: "人材", tone: "soft-success" };
  }
  return { label: sourceType, tone: "soft-neutral" };
}

type SortableHeaderProps = {
  label: string;
  sortKey: SortKey;
  currentSort: SortKey;
  currentOrder: SortOrder;
  onSortChange: (sort: SortKey, order: SortOrder) => void;
  align?: "left" | "right";
};

/**
 * クリックでソート切替可能なヘッダーセル
 * @param {SortableHeaderProps} props ヘッダー props
 * @return {JSX.Element} HeaderCell
 */
const SortableHeader: FC<SortableHeaderProps> = ({
  label,
  sortKey,
  currentSort,
  currentOrder,
  onSortChange,
  align,
}) => {
  const isActive = currentSort === sortKey;
  const ariaSort: "ascending" | "descending" | "none" = isActive
    ? currentOrder === "asc"
      ? "ascending"
      : "descending"
    : "none";
  return (
    <Table.HeaderCell
      align={align}
      isSortable
      sortDirection={isActive ? currentOrder : false}
      aria-sort={ariaSort}
      onClick={() => {
        const nextOrder: SortOrder =
          isActive && currentOrder === "desc" ? "asc" : "desc";
        onSortChange(sortKey, nextOrder);
      }}
    >
      {label}
    </Table.HeaderCell>
  );
};

/**
 * マッチング評価一覧のテーブル本体。
 * @param {FeedbackTableProps} props テーブル props
 * @return {JSX.Element} テーブル UI
 */
export const FeedbackTable: FC<FeedbackTableProps> = ({
  items,
  loading,
  sort,
  order,
  onSortChange,
}) => {
  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
      <Table.Root density="compact" zebra stickyHeader>
        <Table.Head sticky>
          <tr>
            <SortableHeader
              label="評価日時"
              sortKey="created_at"
              currentSort={sort}
              currentOrder={order}
              onSortChange={onSortChange}
            />
            <Table.HeaderCell>テナント</Table.HeaderCell>
            <Table.HeaderCell>WS</Table.HeaderCell>
            <Table.HeaderCell>評価者</Table.HeaderCell>
            <SortableHeader
              label="★"
              sortKey="rating"
              currentSort={sort}
              currentOrder={order}
              onSortChange={onSortChange}
            />
            <Table.HeaderCell>コメント抜粋</Table.HeaderCell>
            <Table.HeaderCell>元メール件名</Table.HeaderCell>
            <Table.HeaderCell>マッチ先件名</Table.HeaderCell>
            <SortableHeader
              label="スコア"
              sortKey="total_score"
              currentSort={sort}
              currentOrder={order}
              onSortChange={onSortChange}
              align="right"
            />
            <Table.HeaderCell>ステータス</Table.HeaderCell>
            <Table.HeaderCell align="right">操作</Table.HeaderCell>
          </tr>
        </Table.Head>
        <Table.Body>
          {loading ? (
            <tr>
              <td colSpan={COL_COUNT} className="text-center py-8">
                <Loading size="md" />
              </td>
            </tr>
          ) : items.length === 0 ? (
            <tr>
              <td
                colSpan={COL_COUNT}
                className="text-center py-8 text-gray-500"
              >
                該当する評価データがありません
              </td>
            </tr>
          ) : (
            items.map(item => {
              const evaluator = item.createdBy
                ? `${item.createdBy.familyName} ${item.createdBy.givenName}`
                : "(退会済み)";
              const tone = statusTone(item.result.status);
              const statusLabel =
                STATUS_LABELS[item.result.status] ?? item.result.status;
              const srcBadge = sourceTypeBadge(item.result.sourceType);
              const detailHref = `/emails/search/${item.result.sourceEmailId}`;
              // 低評価行 (★1〜2) は要注意として薄い赤背景でハイライト
              const isLowRating = item.rating <= 2;
              // Table.Row が className を omit しているため、style で背景色を当てる。
              // rose-50 相当 (#fff1f2)。zebra より優先される。
              const rowStyle: CSSProperties | undefined = isLowRating
                ? { backgroundColor: "#fff1f2" }
                : undefined;
              return (
                <Table.Row key={item.id} style={rowStyle}>
                  <Table.Cell>
                    <span className="whitespace-nowrap text-gray-700">
                      {formatDateTime(item.createdAt)}
                    </span>
                  </Table.Cell>
                  <Table.Cell>{item.tenant.name}</Table.Cell>
                  <Table.Cell>
                    <span className="text-gray-600">{item.workspace.name}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex flex-col">
                      <span>{evaluator}</span>
                      {item.createdBy && (
                        <span className="text-xs text-gray-500">
                          {item.createdBy.email}
                        </span>
                      )}
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <span
                      className="text-amber-500"
                      title={`${item.rating} / 5`}
                      role="img"
                      aria-label={`${item.rating}つ星`}
                    >
                      {"★".repeat(item.rating)}
                      <span className="text-slate-300" aria-hidden="true">
                        {"★".repeat(5 - item.rating)}
                      </span>
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span
                      className="text-gray-700"
                      title={item.comment ?? undefined}
                    >
                      {previewComment(item.comment)}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex items-center gap-2">
                      <StatusBadge tone={srcBadge.tone}>
                        {srcBadge.label}
                      </StatusBadge>
                      <Link
                        to={detailHref}
                        className="text-primary hover:underline truncate max-w-[18rem]"
                        title={item.result.sourceSubject ?? undefined}
                      >
                        {item.result.sourceSubject ?? "(件名なし)"}
                      </Link>
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    {item.result.matchedEmailId ? (
                      <Link
                        to={`/emails/search/${item.result.matchedEmailId}`}
                        className="text-primary hover:underline truncate max-w-[18rem] inline-block align-middle"
                        title={item.result.matchedSubject}
                      >
                        {item.result.matchedSubject}
                      </Link>
                    ) : (
                      <span
                        className="text-gray-700 truncate max-w-[18rem] inline-block align-middle"
                        title={item.result.matchedSubject}
                      >
                        {item.result.matchedSubject}
                      </span>
                    )}
                  </Table.Cell>
                  <Table.Cell align="right">
                    <span className="font-mono">
                      {item.result.totalScore.toFixed(2)}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <StatusBadge tone={tone}>{statusLabel}</StatusBadge>
                  </Table.Cell>
                  <Table.Cell align="right">
                    <Button asChild variant="ghost" size="xs">
                      <Link to={detailHref}>詳細</Link>
                    </Button>
                  </Table.Cell>
                </Table.Row>
              );
            })
          )}
        </Table.Body>
      </Table.Root>
    </div>
  );
};
